import type { LLMAnalysisResult, LLMAnalysisResultItem, LLMMetadata } from "src/modules/ANNModule/ANNLLMAnalyzer";
import { getAiFieldText, SERVITUT_ANALYSIS_FIELDS, type ServitutAnalysisFieldKey } from "src/modules/ANNModule/ANNServitutFieldAnnotations";
import { statusFromConfidence, type ServitutAiFieldStatus } from "src/modules/ANNModule/ANNServitutProvenance";
import { isFetched, type ServitutRow } from "../model/row";
import type { ContentRowResult, LocateRowResult } from "../store/actions";

export function withSkippedPaataleResolution(metadata: LLMMetadata): LLMMetadata {
   return { ...metadata, skipPaataleberettigetResolution: true };
}

export function locateResultsFromAnalysis(
   rows: ServitutRow[],
   result: LLMAnalysisResult,
   onlyRowIds?: Set<string>,
): LocateRowResult[] {
   const byId = new Map<string, LLMAnalysisResultItem>();
   const byAktId = new Map<string, LLMAnalysisResultItem>();
   result.items?.forEach((item) => {
      if (item.id) {
         byId.set(item.id, item);
      }
      if (item.aktId) {
         byAktId.set(item.aktId, item);
      }
   });
   return rows.flatMap((row) => {
      if (onlyRowIds && !onlyRowIds.has(row.id)) {
         return [];
      }
      const item = byId.get(row.id)
         || (row.document.kind === "tingbog_akt" ? byAktId.get(row.document.aktId) : undefined)
         || (row.identity.aktId ? byAktId.get(row.identity.aktId) : undefined);
      return item ? [{ rowId: row.id, item: { ...item, id: row.id } }] : [];
   });
}

function fieldStatus(
   item: LLMAnalysisResultItem | undefined,
   field: ServitutAnalysisFieldKey,
   canRun: boolean,
): ServitutAiFieldStatus {
   if (field === "resume") {
      return statusFromConfidence(item?.overall_confidence, false, Boolean(String(item?.summary || "").trim()), canRun);
   }
   if (field === "entitledParty") {
      return statusFromConfidence(
         item?.paataleberettiget_confidence,
         false,
         Boolean(item?.paataleberettiget || item?.paataleberettiget_resolution),
         canRun,
      );
   }
   if (field === "condition") {
      return statusFromConfidence(item?.servitut_type_confidence, false, Boolean(item?.servitut_type), canRun);
   }
   return statusFromConfidence(
      item?.case_relevance_confidence,
      false,
      item?.case_relevant !== undefined || Boolean(item?.case_relevance_reasoning),
      canRun,
   );
}

export function contentResultFromItem(
   row: ServitutRow,
   item: LLMAnalysisResultItem,
   caseDescription?: string | null,
): ContentRowResult {
   const stamped = {
      ...item,
      id: row.id,
      case_description: caseDescription?.trim() || item.case_description,
   };
   const fieldStatuses = {} as Record<ServitutAnalysisFieldKey, ServitutAiFieldStatus>;
   const fieldTexts = {} as Record<ServitutAnalysisFieldKey, string>;
   SERVITUT_ANALYSIS_FIELDS.forEach((field) => {
      fieldStatuses[field] = fieldStatus(stamped, field, isFetched(row));
      fieldTexts[field] = getAiFieldText(field, stamped, { caseDescription: stamped.case_description || undefined });
   });
   return { rowId: row.id, item: stamped, fieldStatuses, fieldTexts };
}

export function stampContentCaseDescription(
   result: LLMAnalysisResult,
   caseDescription: string | null,
): LLMAnalysisResult {
   const stamped = caseDescription?.trim() || null;
   return {
      ...result,
      items: (result.items || []).map((item) => ({ ...item, case_description: stamped })),
   };
}
